import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CSSTransition } from 'react-transition-group';
import onResize from 'simple-element-resize-detector';
import { toastr } from "react-redux-toastr";
import moment from "moment";


import NoZonesComponent from "./NoZonesComponent";
import ZonesPopover from "./ZonesPopover";
import { getCompanyZones } from "../../../action/CompaniesActions";
import CustomButton from "../../CustomButton";
import { ReactComponent as ZonesBtnIcon } from '../../../../../assets/zones-btn.svg';
import { getPolygonAreaAcres } from "../../helpers/helperZones";

export default function ZonesMapComponent() {
    const dispatch = useDispatch();
    const { t } = useTranslation('translation', { keyPrefix: 'zonesPage' });
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const behaviorRef = useRef(null);
    const platformRef = useRef(null);
    const animationTokens = useRef(new Map());
    const [mapUI, setMapUI] = useState(null);
    const [mapState, setMapState] = useState({
        lat: 37.09024,
        lng: -95.712891,
        zoom: 12,
    });
    const zones = useSelector(state => state.zones.zonesList);
    const mode = useSelector(state => state.zones.mode);
    const [mapPopOver, setMapPopOver] = useState(true);
    const user = useSelector(state => state.user.user);
    const company = useSelector(state => state.company.company);
    const companyId = company.company_id;
    const searchText = useSelector(state => state.zones.searchText);
    const highlightedZoneId = useSelector(state => state.zones.highlightedZoneId);
    const { settings = {} } = user;
    const userTZ = settings.time_zone || null;
    const filteredZones = useMemo(() => {
        return zones.filter(zone =>
            zone.zone_name.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [zones, searchText]);

    useEffect(() => {
        if (companyId) {
            toggleLoader(true);
            getZones();
        }
    }, [companyId]);

    useEffect(() => {
        if (!highlightedZoneId) return;

        const timer = setTimeout(() => {
            setHighlightedZoneId(null);
        }, 3000);

        return () => clearTimeout(timer);
    }, [highlightedZoneId]);

    const setZonesList = useCallback((payload) => {
        dispatch({ type: 'SET_ZONES_LIST', payload })
    }, [dispatch]);

    const setMode = useCallback((payload) => {
        dispatch({ type: 'SET_MODE', payload })
    }, [dispatch]);

    const setHighlightedZoneId = useCallback((payload) => {
        dispatch({ type: 'SET_HIGHLIGHTED_ZONE_ID', payload })
    }, [dispatch]);

    const toggleLoader = useCallback((show) => {
        dispatch({ type: 'TOGGLE_LOADER', payload: show });
    }, [dispatch]);

    const getZones = (stayAtZone) => {
        const localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const params = {
            date: moment().format('YYYYMMDDhhmmss'),
            tzname: userTZ ? userTZ : localTZ,
        };
        getCompanyZones(companyId, params).then(res => {
            const map = mapInstanceRef?.current;
            const list = extendZonesWithArea(
                res.data.response || []
            );
            setZonesList(list);
            setMode(list.length ? 'LIST' : 'NO_ZONES');
            if (map && !stayAtZone) {
                if (list.length > 0) {
                    const allZonesObject = map.getObjects().find(
                        obj => obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
                    );
                    map.getViewModel().setLookAtData({ bounds: allZonesObject?.getBoundingBox() });
                }
            }
            toggleLoader(false);
        }).catch(err => {
            console.log(err, "get Zones error");
            toastr.error(t('errorLoadingZones'))
            toggleLoader(false);
        });
    }

    useEffect(() => {
        if (!mapInstanceRef.current) {
            const platform = new H.service.Platform({
                apikey: process.env.REACT_APP_HERE_MAPS_API_KEY
            });
            platformRef.current = platform;
            const layers = platform.createDefaultLayers();
            const baseProvider = layers.vector.normal.map.getProvider();
            const noWrapLayer = new H.map.layer.TileLayer(baseProvider, {
                worldWrap: false,
                max: 22
            });
            const MAP = new H.Map(mapRef.current, noWrapLayer, {
                center: {
                    lat: mapState.lat,
                    lng: mapState.lng
                },
                zoom: mapState.zoom,
                pixelRatio: window.devicePixelRatio || 1
            });

            onResize(mapRef.current, () => MAP.getViewPort().resize());
            MAP.addEventListener('mapviewchange', handleMapViewChange);
            behaviorRef.current = new H.mapevents.Behavior(new H.mapevents.MapEvents(MAP));
            const ui = H.ui.UI.createDefault(MAP, layers);
            ui.getControl('zoom').setAlignment('right-top');
            setMapUI(ui);

            mapInstanceRef.current = MAP;
        }

        return () => {
            mapInstanceRef.current?.removeEventListener('mapviewchange', handleMapViewChange);
        }
    }, []);

    useEffect(() => {
        if (mapInstanceRef?.current && mapUI?.current) {
            mapUI.getControl('zoom').setAlignment('left-top');
        }

        return () => {
            if (mapInstanceRef?.current && mapUI?.current) {
                mapUI.current.getControl('zoom').setAlignment('right-top');
                mapInstanceRef.current.removeObjects(mapInstanceRef.current.getObjects().filter(e => e && e instanceof H.map.Group));
            }
        }
    }, []);

    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map) return;

        const allZonesGroup = map.getObjects().find(obj =>
            obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
        );
        if (allZonesGroup) {
            allZonesGroup.getObjects().forEach(zoneGroup => {
                zoneGroup.getObjects().forEach(obj => {
                    if (obj instanceof H.map.DomMarker) {
                        obj.setVisibility(mapState.zoom >= 10);
                    }
                });
            });
        }
    }, [filteredZones, mapState.zoom]);

    useEffect(() => {
        const map = mapInstanceRef?.current;
        if (!map) return;

        const zoneGroups = map.getObjects().filter(obj =>
            obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
        );
        map.removeObjects(zoneGroups);

        const allZonesObject = new H.map.Group();
        allZonesObject.setRemoteId('allZonesObject');
        const shapesToHighlight = [];

        const k = (v, radius) => Math.round(v / 2 + radius * v / 10);

        filteredZones.forEach(zone => {
            const group = new H.map.Group();
            group.setRemoteId(zone.id);
            let shape;

            if (zone.shape_type === 'CIRCLE') {
                const position = { lat: zone.shape_points[0].lat, lng: zone.shape_points[0].lng };
                const div = document.createElement('div');
                const label = document.createElement('label');
                div.setAttribute('class', 'markerZone');

                label.style.fontSize = `${k(18, zone.circle_radius)}px`;
                label.style.marginTop = `25px`;
                label.innerText = zone.zone_name;
                div.appendChild(label);

                const domIcon = new H.map.DomIcon(div);
                const textLabel = new H.map.DomMarker(position, { icon: domIcon });
                textLabel.setData({ domElement: div });

                const size = { w: k(38, zone.circle_radius), h: k(55, zone.circle_radius) };
                const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="${size.w}" height="${size.h}" viewBox="0 0 24 24"><path fill="${zone.shape_color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
                const icon = new H.map.Icon(svgIcon, { size });
                const marker = new H.map.Marker(position, { icon });

                const circle = new H.map.Circle(position, (zone.circle_radius * 1609.34), {
                    style: {
                        fillColor: `${zone.shape_color}55`,
                        lineWidth: 0
                    }
                });

                shape = circle;
                group.addObjects([marker, textLabel, circle]);
            } else if (zone.shape_type === 'POLYGON') {
                const points = zone.shape_points;
                if (!Array.isArray(points) || points.length < 3) {
                    console.warn('Skipping POLYGON zone with invalid shape_points', zone);
                    return;
                }

                const strip = new H.geo.LineString();
                points.forEach(p => {
                    strip.pushLatLngAlt(Number(p.lat), Number(p.lng), 0);
                });
                const centerLat = points.reduce((sum, p) => sum + Number(p.lat), 0) / points.length;
                const centerLng = points.reduce((sum, p) => sum + Number(p.lng), 0) / points.length;
                const centerPosition = { lat: centerLat, lng: centerLng };

                const geoPolygon = new H.geo.Polygon(strip);
                const polygon = new H.map.Polygon(geoPolygon, {
                    style: {
                        fillColor: `${zone.shape_color}55`,
                        lineWidth: 0
                    }
                });

                const div = document.createElement('div');
                const label = document.createElement('label');
                div.setAttribute('class', 'markerZone');
                label.style.fontSize = '18px';
                label.style.marginTop = '25px';
                label.innerText = zone.zone_name;
                div.appendChild(label);

                const domIcon = new H.map.DomIcon(div);
                const textLabel = new H.map.DomMarker(centerPosition, { icon: domIcon });
                textLabel.setData({ domElement: div });

                const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="55" viewBox="0 0 24 24"><path fill="${zone.shape_color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
                const pinMarker = new H.map.Marker(centerPosition, {
                    icon: new H.map.Icon(svgIcon, { size: { w: 38, h: 55 } })
                });

                shape = polygon;
                group.addObjects([polygon, pinMarker, textLabel]);
            } else {
                return;
            }

            group.addEventListener('tap', () => handleMapClick(shape, zone.id, zone.shape_color));
            allZonesObject.addObject(group);
            shapesToHighlight.push({ shape, color: zone.shape_color });
        });
        map.addObject(allZonesObject);

        map.getViewPort().setPadding(15, (window.innerWidth > 568 ? 238 : 182), 15, 15);

        shapesToHighlight.forEach(({ shape, color }) => changeStyle(shape, color));
    }, [filteredZones]);

    const handleMapClick = (shape, zoneId, color) => {
        changeStyle(shape, color);
        setHighlightedZoneId(zoneId);
    };

    const changeStyle = (shape, color) => {
        const token = Symbol('zone-animation');
        animationTokens.current.set(shape, token);

        const fillColor = `${color}55`;

        shape.setStyle({
            fillColor,
            lineWidth: 20,
            strokeColor: `${color}85`
        });

        setTimeout(() => {
            setTimeout(() => {
                if (animationTokens.current.get(shape) === token) {
                    fadeOutCircle(shape, 2000, color, token);
                }
            }, 3000);
        }, 50);
    };

    const fadeOutCircle = (shape, duration = 2000, color, token) => {
        const start = performance.now();
        const fillColor = `${color}55`;
        const startAlpha = 0x85;

        const animate = (time) => {
            if (animationTokens.current.get(shape) !== token) {
                return;
            }

            const elapsed = time - start;
            const progress = Math.min(elapsed / duration, 1);
            const newWidth = 20 - (20 * progress);
            const newAlpha = Math.round(startAlpha * (1 - progress));
            const alphaHex = newAlpha.toString(16).padStart(2, '0');

            shape.setStyle({
                fillColor,
                lineWidth: newWidth,
                strokeColor: `${color}${alphaHex}`
            });

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                shape.setStyle({ fillColor, lineWidth: 0, strokeColor: `${color}00` });
                animationTokens.current.delete(shape);
            }
        };

        requestAnimationFrame(animate);
    };

    const handleMapViewChange = (e) => {
        if (e.newValue?.lookAt) {
            const { position, zoom } = e.newValue.lookAt;
            const lat = Math.trunc(position.lat * 1e7) / 1e7;
            const lng = Math.trunc(position.lng * 1e7) / 1e7;
            const z = Math.trunc(zoom * 1e2) / 1e2;
            setMapState({ lat, lng, zoom: z });
        }
    };

    const getZoom = (radius) => {
        if (radius >= 0.1 && radius <= 0.5) {
            return 15;
        } else if (radius > 0.5 && radius <= 1) {
            return 14;
        } else if (radius > 1 && radius <= 2) {
            return 13;
        }
        return 12;
    }

    const highlightZoneOnMap = (id) => {
        const map = mapInstanceRef?.current;
        if (!map) return;

        const allZonesGroup = map.getObjects().find(
            obj => obj instanceof H.map.Group && obj.getRemoteId() === 'allZonesObject'
        );
        const zoneGroup = allZonesGroup.getObjects().find(
            obj => obj instanceof H.map.Group && obj.getRemoteId() === id
        );
        const zone = filteredZones.find(z => z.id === id);
        if (zoneGroup) {
            zoneGroup.getObjects().forEach(obj => {
                if (obj instanceof H.map.Circle || obj instanceof H.map.Polygon) {
                    const boundingBox = zoneGroup.getBoundingBox();
                    const center = boundingBox.getCenter();
                    const handleViewChangeEnd = () => {
                        changeStyle(obj, zone?.shape_color);
                        map.removeEventListener('mapviewchangeend', handleViewChangeEnd);
                    };

                    map.addEventListener('mapviewchangeend', handleViewChangeEnd);
                    map.getViewModel().setLookAtData({
                        position: { lat: center.lat, lng: center.lng },
                        zoom: 8
                    }, true);
                }
            });
        }
    };

    const extendZonesWithArea = (zones = []) =>
        zones.map(zone => {
            if (zone.shape_type !== 'POLYGON') {
                return zone;
            }

            return {
                ...zone,
                area: getPolygonAreaAcres(zone.shape_points)
            };
        });

    return (
        <div className="map-wrapper">
            <div
                id="map"
                style={{ position: 'relative', width: '100%', height: 'calc(100vh - 67px)' }}
                ref={mapRef}
            ></div>
            {['LIST', 'CREATE', 'EDIT'].includes(mode) ? (
                <>
                    <CustomButton
                        variant={mapPopOver ? "primary-outline" : "primary"}
                        size="icon"
                        prefix="zones-popover-button"
                        onClick={() => setMapPopOver(!mapPopOver)}
                    >
                        <ZonesBtnIcon />
                    </CustomButton>
                    <CSSTransition
                        in={mapPopOver}
                        timeout={400}
                        classNames="popover-transition"
                        unmountOnExit
                    >
                        <div>
                            <ZonesPopover
                                getZones={getZones}
                                getZoom={getZoom}
                                map={mapInstanceRef?.current}
                                behavior={behaviorRef?.current}
                                mapState={mapState}
                                platform={platformRef?.current}
                                highlightZoneOnMap={highlightZoneOnMap}
                            />
                        </div>
                    </CSSTransition>
                </>
            ) : ''}
            {mode === 'NO_ZONES' ? (
                <NoZonesComponent />
            ) : ''}
        </div>
    )
}
